// =============================================================================
//  Tenants de demonstration + reference pricing.
//
//  Modele single-tenant : chaque compte NIS365 audite UN tenant M365 unique.
//  En demo on garde 3 personae pour les screenshots/presentations, mais le
//  TenantSwitcher est retire de l'UI -> seul le 1er est jamais affiche.
//
//  Pricing : flat par taille de tenant audite (pas per-seat). Cf. les 4
//  tranches definies dans planTiers ci-dessous.
// =============================================================================

import { SubscriptionPlan, Tenant } from './types';

export const mockTenants: Tenant[] = [
  {
    id:               'tnt-contoso',
    name:             'Contoso Industries',
    domain:           'contoso.onmicrosoft.com',
    m365TenantId:     'd9f6e1a4-1a2b-4c3d-9e8f-1234567890ab',
    industry:         'Industrie manufacturiere',
    size:             850,                  // -> tier Business (251-2500)
    subscriptionPlan: 'business',
    createdAt:        '2025-09-15T09:00:00Z',
    lastScanAt:       '2026-05-26T07:42:11Z',
    lastScore:        68,
  },
  {
    id:               'tnt-fabrikam',
    name:             'Fabrikam Health',
    domain:           'fabrikam.health',
    m365TenantId:     '7b3c8d2e-4f5a-6b7c-8d9e-0fa1b2c3d4e5',
    industry:         'Sante (entite essentielle NIS2)',
    size:             2800,                 // -> tier Enterprise (>2500)
    subscriptionPlan: 'enterprise',
    createdAt:        '2025-06-02T11:30:00Z',
    lastScanAt:       '2026-05-27T04:15:00Z',
    lastScore:        88,
  },
  {
    id:               'tnt-litware',
    name:             'Litware Architects',
    domain:           'litware.eu',
    m365TenantId:     '1234abcd-5678-90ef-1234-567890abcdef',
    industry:         'Services professionnels',
    size:             28,                   // -> tier Starter (<=50)
    subscriptionPlan: 'starter',
    createdAt:        '2026-02-11T15:20:00Z',
    lastScanAt:       '2026-05-20T16:00:00Z',
    lastScore:        42,
  },
];

/** Tenant actif par defaut (sera selectionne au demarrage). */
export const defaultTenantId = 'tnt-contoso';

// ============================================================================
// PRICING : 4 plans flat par tranche de taille tenant
// ============================================================================

export interface PlanTier {
  plan:        SubscriptionPlan;
  label:       string;
  /** Borne MAX inclusive de la tranche (en nombre d'users M365 du tenant). */
  maxSize:     number;
  /** Prix mensuel HT en EUR. null = sur devis (Enterprise). */
  monthlyEur:  number | null;
  description: string;
  /** Features highlight pour la page Abonnement. */
  features:    string[];
}

export const planTiers: PlanTier[] = [
  {
    plan:        'starter',
    label:       'Starter',
    maxSize:     50,
    monthlyEur:  49,
    description: 'Pour les TPE (<= 50 utilisateurs M365). Audit NIS2 + ISO 27001 + CyFun, scans planifies, rapports PDF/DOCX/XLSX.',
    features: [
      'Jusqu\'a 50 utilisateurs M365',
      'Catalogue NIS2 + ISO 27001 + CIS M365 + CyFun (25 controles)',
      'Scans mensuels planifies',
      'Rapports PDF + DOCX + XLSX + JSON evidences',
      'Historique 12 mois',
      'Support email 2 jours ouvres',
    ],
  },
  {
    plan:        'pro',
    label:       'Pro',
    maxSize:     250,
    monthlyEur:  149,
    description: 'Pour les PME (51 a 250 utilisateurs). Scans hebdo, alertes regression, support email J+1.',
    features: [
      'Jusqu\'a 250 utilisateurs M365',
      'Tout Starter inclus',
      'Scans hebdomadaires',
      'Alertes email sur regression de score',
      'Webhooks Slack / Teams',
      'Historique 24 mois',
      'Support email 1 jour ouvre',
    ],
  },
  {
    plan:        'business',
    label:       'Business',
    maxSize:     2500,
    monthlyEur:  499,
    description: 'Pour les ETI (251 a 2500 utilisateurs). Scans quotidiens, incident wizard NIS2 24h, SSO.',
    features: [
      'Jusqu\'a 2500 utilisateurs M365',
      'Tout Pro inclus',
      'Scans quotidiens',
      'Incident wizard NIS2 24h (assistance notification CSIRT)',
      'SSO SAML',
      'API publique read-only',
      'Historique 5 ans',
      'Support email 4h en heures ouvrees',
    ],
  },
  {
    plan:        'enterprise',
    label:       'Enterprise',
    maxSize:     Number.POSITIVE_INFINITY,
    monthlyEur:  null,
    description: 'Pour les grandes organisations (> 2500 utilisateurs) et entites essentielles NIS2. Sur devis.',
    features: [
      'Utilisateurs M365 illimites',
      'Tout Business inclus',
      'API publique full read/write',
      'Integration SIEM (Sentinel, Splunk, Sentinel One)',
      'Rapports white-label (logo client)',
      'Audit trail signe eIDAS',
      'Historique 7 ans (conforme NIS2 art. 23)',
      'SLA 99.9% + support 24/5 + CSM dedie',
    ],
  },
];

/** Lookup direct par plan name. */
export const planTierByPlan: Record<SubscriptionPlan, PlanTier> = Object.fromEntries(
  planTiers.map((t) => [t.plan, t]),
) as Record<SubscriptionPlan, PlanTier>;

/** Trouve le plan correspondant a une taille de tenant donnee. */
export function resolvePlanFromSize(size: number): PlanTier {
  return planTiers.find((t) => size <= t.maxSize) ?? planTiers[planTiers.length - 1];
}

/** Description marketing courte (compat avec ancien code). */
export const planDescription: Record<SubscriptionPlan, string> = Object.fromEntries(
  planTiers.map((t) => [t.plan, t.description]),
) as Record<SubscriptionPlan, string>;
